import WebMap from "@arcgis/core/WebMap";
import { AppConfig } from "../AppConfig";
import { getAGEToken } from "./authenticate";
import Portal from "@arcgis/core/portal/Portal.js";
import PortalQueryParams from "@arcgis/core/portal/PortalQueryParams.js";
import PortalItem from "@arcgis/core/portal/PortalItem.js";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import type PortalUser from "@arcgis/core/portal/PortalUser";

export const getUsers = async () => {
    let usersUrl = AppConfig.ArcGIS.BaseUrl + "/sharing/rest/portals/self/users";
    const paramString = "&excludeSystemUsers=true&num=1000";
    const token = await getAGEToken();

    let users: PortalUser[] = [];
    await fetch(usersUrl + `?f=json${paramString}`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    })
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.users) {
                    users = data.users;
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return users;
};

export const getConfigFromWebMap = async (itemId: string): Promise<WebMap | undefined> => {
    try {
        const portal = new Portal({
            url: AppConfig.ArcGIS.BaseUrl,
            authMode: "immediate",
        });

        if (!portal.loaded) {
            await portal.load();
        }

        const portalQuery = new PortalQueryParams({
            query: `id:${itemId}`,
            num: 1,
        });

        const configMap = await portal.queryItems(portalQuery);

        if (configMap.results.length > 0) {
            const mapItem = configMap.results[0];

            if (mapItem.type === "Web Map") {
                const configWebMap = new WebMap({
                    portalItem: {
                        portal: portal,
                        id: mapItem.id,
                    },
                });

                if (!configWebMap.loaded) {
                    await configWebMap.load();
                }

                return configWebMap;
            }
        } else {
            console.warn("No results found for the given itemId.");
        }
    } catch (error) {
        console.error("Error occurred while fetching the WebMap configuration:", error);
    }
    return undefined;
};

export const getThemeMapsFromGroup = async (groupId: string): Promise<WebMap[]> => {
    try {
        const portal = new Portal({
            url: AppConfig.ArcGIS.BaseUrl,
            authMode: "immediate",
        });

        if (!portal.loaded) {
            await portal.load();
        }

        const portalQuery = new PortalQueryParams({
            query: `group:${groupId}`,
            num: 1000,
        });

        const configMap = await portal.queryItems(portalQuery);

        if (configMap.results.length > 0) {
            const maps: WebMap[] = [];

            for (const mapItem of configMap.results) {
                if (mapItem.type === "Web Map") {
                    const item = new PortalItem({
                        portal: portal,
                        id: mapItem.id,
                    });
                    item.load();
                    const data = (await item.fetchData("json")) as { spatialReference?: { wkid: number } };
                    let spatialReference;
                    if (data.spatialReference) {
                        spatialReference = new SpatialReference({
                            wkid: data.spatialReference.wkid,
                        });
                    }
                    const configWebMap = new WebMap({
                        portalItem: {
                            portal: portal,
                            id: mapItem.id,
                        },
                    });

                    if (!configWebMap.loaded) {
                        await configWebMap.load();
                    }
                    if (spatialReference && configWebMap.basemap) {
                        configWebMap.basemap.spatialReference = spatialReference;
                    }
                    maps.push(configWebMap);
                }
            }
            return maps;
        } else {
            console.warn("No results found for the given groupId.");
        }
    } catch (error) {
        console.error("Error occurred while fetching the WebMaps from group:", error);
    }
    return [];
};

export const getPortalItemsFromGroup = async (groupId: string, tagToFilterBy?: string): Promise<PortalItem[]> => {
    try {
        const portal = new Portal({
            url: AppConfig.ArcGIS.BaseUrl,
            authMode: "immediate",
        });

        if (!portal.loaded) {
            await portal.load();
        }

        const portalQuery = new PortalQueryParams({
            query: `group:${groupId}`,
            num: 1000,
        });

        if (tagToFilterBy) {
            portalQuery.query += ` AND tags:${tagToFilterBy}`;
        }

        const portalItems = await portal.queryItems(portalQuery);
        const layerItems: PortalItem[] = [];
        for (const item of portalItems.results) {
            if ((item.type === "Feature Service" || item.type === "Map Service") && item.isLayer) {
                layerItems.push(item);
            }
        }
        return layerItems;
    } catch (error) {
        console.error("Error occurred while fetching the PortalItems from group:", error);
    }
    return [];
};
